import { useState } from 'react';
import { Box, Button, Checkbox, CircularProgress, FormControlLabel, TextField, Typography } from '@mui/material';
import { inject, observer } from 'mobx-react';
import { toast } from 'react-toastify';
import { compose } from 'utils/compose';
import SmsVerificationModal, { MAX_ATTEMPTS } from './SmsVerificationModal';

const SMS_OPT_IN_RESUBSCRIBE_MESSAGE =
  'If you previously stopped our messages, reply START to our last delivery notification to re-activate updates.';

function SmsOptIn({ store, settings }) {
  const { deliveryStore } = store;

  const [phone, setPhone] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const [callConsent, setCallConsent] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [wasUnsubscribed, setWasUnsubscribed] = useState(false);
  const [attempts, setAttempts] = useState(0);

  // Settings values are strings ("true"/"false"), not booleans — compare explicitly so
  // "false" doesn't slip through as truthy. enforced_sms_enabled is the inverse: only HIDE
  // the section when it's explicitly "true".
  const isFeatureEnabled =
    settings?.enable_recipient_sms_opt_in === 'true' &&
    Boolean(settings?.sms_opt_in_verification_field) &&
    settings?.enforced_sms_enabled !== 'true';

  // Voice-call opt-in is currently not a shipped feature; default hidden. Clients piloting
  // voice-call delivery alerts can flip enable_recipient_call_opt_in="true" via tracking_settings
  // to expose the second checkbox.
  const isCallOptInEnabled = settings?.enable_recipient_call_opt_in === 'true';

  if (!isFeatureEnabled) return null;

  if (submitted) {
    return (
      <Box
        role="alert"
        sx={{
          border: '1px solid',
          borderColor: 'success.main',
          p: 3,
          mb: 3,
        }}
      >
        <Typography variant="body1" color="success.main" fontWeight={600}>
          You're signed up for delivery updates!
        </Typography>
        {wasUnsubscribed && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {settings?.sms_opt_in_resubscribe_message || SMS_OPT_IN_RESUBSCRIBE_MESSAGE}
          </Typography>
        )}
      </Box>
    );
  }

  const validatePhone = (value) => {
    const digits = value.replace(/\D/g, '');
    return digits.length >= 10 && digits.length <= 15;
  };

  const handleSignUp = () => {
    if (!validatePhone(phone)) {
      setPhoneError('Please enter a valid phone number.');
      return;
    }
    setPhoneError('');
    setModalOpen(true);
  };

  const handleVerificationSuccess = async () => {
    setSubmitting(true);
    try {
      // Defensive: never submit callConsent=true if the call opt-in feature is hidden.
      // The checkbox is also hidden + reset elsewhere, but this belts-and-braces the contract.
      const effectiveCallConsent = isCallOptInEnabled && callConsent;
      const res = await deliveryStore.submitSmsOptIn(phone.trim(), smsConsent, effectiveCallConsent);
      setWasUnsubscribed(res.data?.was_unsubscribed === true);
      setModalOpen(false);
      setSubmitted(true);
    } catch (e) {
      toast.error('Something went wrong. Please try again.', {
        hideProgressBar: true,
        theme: 'colored',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          p: 3,
          mt: 3,
          mb: 3,
        }}
      >
        <Typography variant="h6" fontWeight={700} gutterBottom>
          Get Delivery Updates by SMS
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Mobile Phone Number
        </Typography>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="e.g. +1 (555) 123-4567"
          value={phone}
          onChange={(e) => {
            setPhone(e.target.value);
            if (phoneError) setPhoneError('');
          }}
          error={!!phoneError}
          helperText={phoneError}
          disabled={submitting}
          slotProps={{ htmlInput: { 'aria-label': 'Mobile phone number', inputMode: 'tel' } }}
          sx={{ mb: 1 }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={smsConsent}
              onChange={(e) => setSmsConsent(e.target.checked)}
              disabled={submitting}
              color="primary"
              size="small"
            />
          }
          label={<Typography variant="body2" component="span">Text me with delivery updates</Typography>}
          sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}
        />
        {isCallOptInEnabled && (
          <FormControlLabel
            control={
              <Checkbox
                checked={callConsent}
                onChange={(e) => setCallConsent(e.target.checked)}
                disabled={submitting}
                color="primary"
                size="small"
              />
            }
            label={<Typography variant="body2" component="span">Call me with delivery issues</Typography>}
            sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
          />
        )}
        <Button
          fullWidth
          variant="contained"
          color="primary"
          disableElevation
          disabled={
            !phone.trim() ||
            (!smsConsent && !(isCallOptInEnabled && callConsent)) ||
            submitting ||
            attempts >= MAX_ATTEMPTS
          }
          onClick={handleSignUp}
          sx={{
            borderRadius: '40px',
            textTransform: 'initial',
            fontWeight: 600,
          }}
        >
          {submitting ? <CircularProgress size={20} color="inherit" /> : 'Sign Up for Updates'}
        </Button>
        {attempts >= MAX_ATTEMPTS && (
          <Typography variant="body2" color="error" sx={{ mt: 1, textAlign: 'center' }} role="alert">
            Too many incorrect attempts. Please contact support for assistance.
          </Typography>
        )}
      </Box>

      <SmsVerificationModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={handleVerificationSuccess}
        attempts={attempts}
        onAttemptsChange={setAttempts}
        settings={settings}
      />
    </>
  );
}

export default compose(inject('store'), observer)(SmsOptIn);
